export enum SortBy {
  Value = `value`,
  Label = `label`,
  Column = `column`,
}

export enum SortOrder {
  Ascending = `asc`,
  Descending = `desc`,
}

/**
 * For bar chart and Insights table, this defines the rows to be shown in the table. For other data tables, this
 * defines the segments that are selected. Another way of thinking of it it controls the segments to be shown in the
 * chart viz. The segments chosen are the top segments based on current SortConfig.
 */
type ViewNLimit = number | null;

export type FlatLabelSortConfig = {
  sortBy: SortBy.Label;
  sortOrder: SortOrder;
  viewNLimit?: ViewNLimit;
};
export type FlatValueSortConfig = {
  sortBy: SortBy.Value;
  sortOrder: SortOrder;
  // valueField required when sorting by value (sortBy.Value) and there is > 1 valueField. Used for data tables.
  valueField?: string;
  viewNLimit?: ViewNLimit;
};
export type FlatSortConfig = FlatLabelSortConfig | FlatValueSortConfig;
/** sort by segment columns (segment-grouped sort) */
export type SortByColumnsConfig = {
  sortBy: SortBy.Column;
  // value field to specify which value to sort secondarily on
  valueField?: string;
  colSortAttrs: FlatSortConfig[];
};
/** sort by a single value column (flat sort). Still persists colSortAttrs so that if we switch back to sorting by
 * columns, we preserve the prior sorts on the columns */
export type SortByValueConfig = {
  sortBy: SortBy.Value;
  sortOrder: SortOrder;
  // valueField required when sorting by value (sortBy.Value) and there is > 1 valueField. Used for data tables.
  valueField?: string;
  colSortAttrs: FlatSortConfig[];
  viewNLimit?: ViewNLimit;
};
export type SortConfig = SortByColumnsConfig | SortByValueConfig;

/* Represents the data summarized for unsegmented, or the sum of selected child segments */
interface SumTreeSummaryValues {
  value: number;
  timeComparisonValue: number;
  liftComparisonValue?: number;
  baselineComparisonValue?: number;
}

interface SumTreeSegment {
  label: string;
  /* In a root segment, this represents the unsegmented value from Arb. Otherwise it is a leaf value */
  value: number;
  /* In a root segment, this represents the unsegmented value from Arb. Otherwise it is a leaf value */
  timeComparisonValue: number;
  /* Only ever present in a leaf value */
  liftComparisonValue?: number;
  baselineComparisonUnit?: string;
  /* Should only ever be present in a leaf value */
  baselineComparisonValue?: number;
  headerType: HeaderType;
  path: string[];
}

/**
 * SumTree where children at each depth are a nested object. Necessary for quick segment lookups by path
 * such as accessing time comparison segments or tooltip stats.
 */
export interface UnorderedSumTree {
  [label: string]: UnorderedSumTreeNode;
}

export interface UnorderedSumTreeNode extends SumTreeSegment {
  children?: UnorderedSumTree;
  unsegmented?: SumTreeSummaryValues;
  sumOfChildren?: SumTreeSummaryValues;
}

/**
 * SumTree where children at each depth are an array. Necessary for manipulating and displaying ordered data
 * where there could be children with duplicate segments e.g. when sorting by value.
 */
export interface OrderedSumTree extends SumTreeSegment {
  children?: OrderedSumTree[];
  unsegmented?: SumTreeSummaryValues;
  sumOfChildren?: SumTreeSummaryValues;
}

export enum HeaderType {
  DATE_ISO = `date_iso`,
  DATE_TYPECAST_STRING = `date_typecast_string`,
  DATE_HOURLY = `date_hourly`,
  DATE_DAILY = `date_daily`,
  DATE_WEEKLY = `date_weekly`,
  DATE_MONTHLY = `date_monthly`,
  DATE_QUARTERLY = `date_quarterly`,
  DATE_DAY_OF_WEEK = `date_day_of_week`,
  NUMBER = `number`,
  NUMBER_RANGE = `number_range`,
  STRING = `string`,
}
